import React, { useState } from 'react';
import { Button } from '../../../shared/components';
import { Download, FileExcel, FilePdf, Printer, CheckCircle, ExclamationCircle } from 'react-bootstrap-icons';

const ExportButtons = ({ 
  onExportCSV, 
  onExportExcel, 
  onExportPDF, 
  onPrint,
  disabled = false,
  showRecordCount = false,
  recordCount = 0,
  loadingStates = {},
  size = 'sm',
  variant = 'secondary'
}) => {
  const [exportStatus, setExportStatus] = useState({
    csv: { loading: false, success: false, error: false },
    excel: { loading: false, success: false, error: false },
    pdf: { loading: false, success: false, error: false },
    print: { loading: false, success: false, error: false }
  });

  const handleExport = async (type, exportFunction) => {
    if (!exportFunction) return;
    
    // Reset status for this type
    setExportStatus(prev => ({
      ...prev,
      [type]: { loading: true, success: false, error: false }
    }));

    try {
      await exportFunction();
      setExportStatus(prev => ({
        ...prev,
        [type]: { loading: false, success: true, error: false }
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setExportStatus(prev => ({
          ...prev,
          [type]: { loading: false, success: false, error: false }
        }));
      }, 3000);
    } catch (error) {
      console.error(`Error exporting ${type}:`, error);
      setExportStatus(prev => ({
        ...prev,
        [type]: { loading: false, success: false, error: true }
      }));

      // Clear error message after 3 seconds
      setTimeout(() => {
        setExportStatus(prev => ({
          ...prev,
          [type]: { loading: false, success: false, error: false }
        }));
      }, 3000);
    }
  };

  const getButtonContent = (type, icon, label) => {
    const status = exportStatus[type];
    
    if (status.loading) {
      return (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
          <span>Exporting...</span>
        </div>
      );
    }
    
    if (status.success) {
      return (
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span>Exported!</span>
        </div>
      );
    }
    
    if (status.error) {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationCircle className="w-4 h-4" />
          <span>Failed</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
    );
  };

  const isButtonDisabled = (type) => {
    return disabled || 
           exportStatus[type]?.loading || 
           (recordCount === 0 && showRecordCount);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* CSV Export Button */}
      {onExportCSV && (
        <Button
          variant={exportStatus.csv.error ? 'danger' : variant}
          size={size}
          onClick={() => handleExport('csv', onExportCSV)}
          className="flex items-center gap-2 min-w-[100px] transition-all duration-200"
          disabled={isButtonDisabled('csv')}
        >
          {getButtonContent('csv', <Download className="w-4 h-4" />, 'CSV')}
        </Button>
      )}

      {/* Excel Export Button */}
      {onExportExcel && (
        <Button
          variant={exportStatus.excel.error ? 'danger' : variant}
          size={size}
          onClick={() => handleExport('excel', onExportExcel)}
          className="flex items-center gap-2 min-w-[100px] transition-all duration-200"
          disabled={isButtonDisabled('excel')}
        >
          {getButtonContent('excel', <FileExcel className="w-4 h-4" />, 'Excel')}
        </Button>
      )}

      {/* PDF Export Button */}
      {onExportPDF && (
        <Button
          variant={exportStatus.pdf.error ? 'danger' : variant}
          size={size}
          onClick={() => handleExport('pdf', onExportPDF)}
          className="flex items-center gap-2 min-w-[100px] transition-all duration-200"
          disabled={isButtonDisabled('pdf')}
        >
          {getButtonContent('pdf', <FilePdf className="w-4 h-4" />, 'PDF')}
        </Button>
      )}

      {/* Print Button */}
      {/* {onPrint && (
        <Button
          variant={exportStatus.print.error ? 'danger' : variant}
          size={size}
          onClick={() => handleExport('print', onPrint)}
          className="flex items-center gap-2 min-w-[100px] transition-all duration-200"
          disabled={isButtonDisabled('print')}
        >
          {getButtonContent('print', <Printer className="w-4 h-4" />, 'Print')}
        </Button>
      )} */}

      {/* Record Count Badge */}
      {showRecordCount && recordCount > 0 && (
        <span className="text-sm text-gray-500 ml-2 bg-gray-100 px-3 py-1 rounded-full">
          {recordCount} record{recordCount !== 1 ? 's' : ''}
        </span>
      )}

      {/* No Data Warning */}
      {showRecordCount && recordCount === 0 && !disabled && (
        <span className="text-sm text-yellow-600 ml-2 bg-yellow-50 px-3 py-1 rounded-full">
          No data to export
        </span>
      )}
    </div>
  );
};

// Default props
ExportButtons.defaultProps = {
  disabled: false,
  showRecordCount: false,
  recordCount: 0,
  loadingStates: {},
  size: 'sm',
  variant: 'secondary'
};

export default ExportButtons;